(() => {
  if (window.JanelaPairsWellWithSyncV2) return;
  window.JanelaPairsWellWithSyncV2 = true;

  const productCache = new Map();
  const fallbackOptionNames = ["Color", "Colour", "Pattern", "Print", "Style"];

  class PairsWellWithSyncController {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector("[data-grid]");
      this.mainOptionPosition = Number(root.dataset.mainOptionPosition || 0);
      this.initialOptionValue = String(root.dataset.initialOptionValue || "").trim();
      this.optionNames = Array.from(
        new Set(
          [root.dataset.syncOptionName, ...fallbackOptionNames]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );
      this.lastAppliedOptionValue = "";
      this.syncFrame = null;
      this.syncRequestToken = 0;

      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleMainControlChanged = this.handleMainControlChanged.bind(this);
      this.handleThemeVariantChanged = this.handleThemeVariantChanged.bind(this);
    }

    init() {
      if (!this.grid || !this.mainOptionPosition || this.root.dataset.pairsSyncReady === "true") return;

      this.root.dataset.pairsSyncReady = "true";
      document.addEventListener("variant:changed", this.handleVariantChanged);
      document.addEventListener("change", this.handleMainControlChanged, true);

      if (
        window.MinimogEvents &&
        window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents &&
        window.MinimogTheme.pubSubEvents.variantChange
      ) {
        window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.variantChange,
          this.handleThemeVariantChanged
        );
      }

      if (this.initialOptionValue) {
        this.queueSync(this.initialOptionValue, true);
      }
    }

    normalize(value) {
      return String(value || "").trim().toLowerCase();
    }

    getOptionValueFromVariant(variant) {
      if (!variant) return "";
      return variant[`option${this.mainOptionPosition}`] || "";
    }

    getOptionValueFromMainControl(control) {
      if (!control || !control.closest || this.root.contains(control)) return "";

      const variantPicker = control.closest("variant-picker");
      if (!variantPicker) return "";

      const optionGroup = control.closest("[data-option-name]");
      const optionName = optionGroup?.dataset?.optionName || "";
      const normalizedNames = this.optionNames.map((name) => this.normalize(name));
      if (!normalizedNames.includes(this.normalize(optionName))) return "";

      if (control.matches('input[type="radio"], input[type="checkbox"]') && !control.checked) {
        return "";
      }

      return control.value || control.dataset.value || "";
    }

    handleVariantChanged(event) {
      const optionValue = this.getOptionValueFromVariant(event.detail?.variant);
      if (optionValue) this.queueSync(optionValue);
    }

    handleMainControlChanged(event) {
      const optionValue = this.getOptionValueFromMainControl(event.target);
      if (optionValue) this.queueSync(optionValue);
    }

    handleThemeVariantChanged(payload) {
      const optionValue = this.getOptionValueFromVariant(payload?.data?.variant);
      if (optionValue) this.queueSync(optionValue);
    }

    queueSync(optionValue, force = false) {
      const normalizedValue = String(optionValue || "").trim();
      if (!normalizedValue) return;
      if (!force && normalizedValue === this.lastAppliedOptionValue && !this.syncFrame) return;

      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      const requestToken = ++this.syncRequestToken;

      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncCards(normalizedValue, requestToken);
      });
    }

    getSortableCardNode(card) {
      let node = card;
      while (node && node.parentElement && node.parentElement !== this.grid) {
        node = node.parentElement;
      }
      return node?.parentElement === this.grid ? node : card;
    }

    ensureCardOrder(cards) {
      cards.forEach((card, index) => {
        const node = this.getSortableCardNode(card);
        if (node && !node.dataset.matchSortBaseIndex) {
          node.dataset.matchSortBaseIndex = String(index);
        }
      });
    }

    sortCards(cards, matches) {
      const ordered = cards
        .map((card, index) => ({
          card,
          node: this.getSortableCardNode(card),
          matched: Boolean(matches[index]),
          baseIndex: Number(this.getSortableCardNode(card)?.dataset.matchSortBaseIndex || index)
        }))
        .sort((left, right) => {
          if (left.matched !== right.matched) return left.matched ? -1 : 1;
          return left.baseIndex - right.baseIndex;
        });

      ordered.forEach(({ node }) => {
        if (node) this.grid.appendChild(node);
      });

      this.root.dispatchEvent(new CustomEvent("related-products:refresh"));
    }

    getCardHandle(card) {
      const picker = card.querySelector("[data-pcard-variant-picker]");
      if (picker?.dataset?.productHandle) return picker.dataset.productHandle;

      const link = card.querySelector("a.m-product-card__link, a.m-product-card__name");
      if (!link) return "";

      try {
        const url = new URL(link.getAttribute("href"), window.location.origin);
        const parts = url.pathname.split("/").filter(Boolean);
        const productIndex = parts.indexOf("products");
        return productIndex >= 0 ? parts[productIndex + 1] || "" : "";
      } catch (error) {
        return "";
      }
    }

    getProductData(handle) {
      if (!handle) return Promise.resolve(null);
      if (!productCache.has(handle)) {
        const rootPath = window.MinimogSettings?.routes?.root || "/";
        const productUrl = `${rootPath}products/${handle}.js`.replace("//", "/");
        productCache.set(
          handle,
          fetch(productUrl)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        );
      }
      return productCache.get(handle);
    }

    findVariant(productData, optionValue) {
      if (!productData || !Array.isArray(productData.variants)) return null;

      const normalizedNames = this.optionNames.map((name) => this.normalize(name));
      const options = Array.isArray(productData.options) ? productData.options : [];
      const optionIndex = options.findIndex((option) => {
        const name = typeof option === "object" ? option.name : option;
        return normalizedNames.includes(this.normalize(name));
      });
      if (optionIndex < 0) return null;

      const normalizedValue = this.normalize(optionValue);
      return (
        productData.variants.find(
          (variant) => this.normalize(variant.options?.[optionIndex]) === normalizedValue
        ) || null
      );
    }

    buildVariantHref(href, variantId) {
      if (!href || !variantId) return href;
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set("variant", String(variantId));
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return href;
      }
    }

    updateFallbackVariantState(card, variant) {
      if (!variant) return;

      card.dataset.variantId = String(variant.id);
      card.querySelectorAll('[name="id"]').forEach((input) => {
        input.value = String(variant.id);
        input.setAttribute("value", String(variant.id));
        input.dataset.selectedVariant = String(variant.id);
      });
      card
        .querySelectorAll("a.m-product-card__link, a.m-product-card__name, a.m-row-quick-add-button")
        .forEach((link) => {
          const href = link.getAttribute("href");
          if (href) link.setAttribute("href", this.buildVariantHref(href, variant.id));
        });
      card.querySelectorAll(".m-row-quick-add-button[data-product-url]").forEach((button) => {
        button.dataset.productUrl = this.buildVariantHref(
          button.dataset.productUrl,
          variant.id
        );
      });
    }

    getImageFilename(source) {
      if (!source) return "";
      return String(source).split("?")[0].split("/").pop();
    }

    getSizedImageSource(source, width = 533) {
      if (!source) return "";
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set("width", String(width));
        return url.toString();
      } catch (error) {
        return `${source}${source.includes("?") ? "&" : "?"}width=${width}`;
      }
    }

    setImageSource(container, source) {
      if (!container || !source) return false;

      const image = container.querySelector("img");
      if (!image) return false;

      const sizedSource = this.getSizedImageSource(source);
      const responsiveImage = image.closest("responsive-image");
      if (responsiveImage) {
        responsiveImage.src = sizedSource;
        if (typeof responsiveImage.disconnectObserver === "function") {
          responsiveImage.disconnectObserver();
        }
        responsiveImage.setAttribute("intersecting", "true");
      }

      image.removeAttribute("data-src");
      image.removeAttribute("data-srcset");
      image.removeAttribute("srcset");
      if (image.getAttribute("src") !== sizedSource) {
        image.setAttribute("src", sizedSource);
      }

      return true;
    }

    getNextImageSource(productData, source) {
      const images = Array.isArray(productData?.images) ? productData.images : [];
      const filename = this.getImageFilename(source);
      if (!filename || !images.length) return "";

      const currentIndex = images.findIndex(
        (imageSource) => this.getImageFilename(imageSource) === filename
      );
      if (currentIndex < 0 || currentIndex + 1 >= images.length) return "";

      const nextSource = images[currentIndex + 1];
      const nextFilename = this.getImageFilename(nextSource);
      const startsAnotherVariant = (productData.variants || []).some((variant) => {
        const featuredFilename = this.getImageFilename(variant?.featured_image?.src);
        return featuredFilename && featuredFilename !== filename && featuredFilename === nextFilename;
      });

      return startsAnotherVariant ? "" : nextSource;
    }

    updateCardImages(card, productData, variant) {
      const source = variant?.featured_image?.src;
      if (!source) return;

      const mainContainer = card.querySelector(".m-product-card__main-image");
      const hoverContainer = card.querySelector(".m-product-card__next-image");
      this.setImageSource(mainContainer, source);
      const mainImage = mainContainer?.querySelector("img");
      if (mainImage) {
        mainImage.alt = `${productData.title}, ${variant.title}, main product image by Janela Bay`;
      }

      const nextSource = this.getNextImageSource(productData, source);
      if (hoverContainer && nextSource) {
        this.setImageSource(hoverContainer, nextSource);
        const hoverImage = hoverContainer.querySelector("img");
        if (hoverImage) {
          hoverImage.alt = `${productData.title}, ${variant.title}, alternate product image by Janela Bay`;
        }
        hoverContainer.hidden = false;
        card.classList.add("m-product-card--show-next-img");
      } else if (hoverContainer) {
        hoverContainer.hidden = true;
        card.classList.remove("m-product-card--show-next-img");
      }
    }

    updateCardOptionState(card, productData, variant) {
      const options = Array.isArray(productData?.options) ? productData.options : [];
      const normalizedNames = this.optionNames.map((name) => this.normalize(name));
      const optionIndex = options.findIndex((option) => {
        const name = typeof option === "object" ? option.name : option;
        return normalizedNames.includes(this.normalize(name));
      });
      if (optionIndex < 0) return;

      const optionPosition = String(optionIndex + 1);
      const selectedValue = variant.options?.[optionIndex];
      const nodes = Array.from(
        card.querySelectorAll(
          `[data-pcard-variant-picker] [data-option-position="${optionPosition}"]`
        )
      );
      let selectedNode = null;

      nodes.forEach((node) => {
        const value = node.dataset.value || node.value;
        const isSelected = this.normalize(value) === this.normalize(selectedValue);

        if (isSelected) {
          node.dataset.selected = "true";
          if ("checked" in node) node.checked = true;
          selectedNode = node;
        } else {
          delete node.dataset.selected;
          if ("checked" in node) node.checked = false;
        }
      });

      const swatch = card.querySelector("pcard-swatch");
      if (swatch) {
        swatch.currentVariant = variant;
        if (swatch.activeOptionNodeByPosition && selectedNode) {
          swatch.activeOptionNodeByPosition[optionPosition] = selectedNode;
        }
      }
    }

    updateCardPrice(card, variant) {
      if (!variant || window.MinimogSettings?.pcard_show_lowest_prices) return;

      const priceWrapper = card.querySelector(".m-price");
      const salePrice = card.querySelector(".m-price-item--sale");
      const compareAtPrices = Array.from(card.querySelectorAll(".m-price-item--regular"));
      const moneyFormat = window.MinimogSettings?.money_format;
      const price = Number(variant.price) || 0;
      const compareAtPrice = Number(variant.compare_at_price) || 0;
      const onSale = compareAtPrice > price;

      priceWrapper?.classList.toggle("m-price--on-sale", onSale);
      priceWrapper?.classList.toggle("m-price--sold-out", !variant.available);
      priceWrapper?.classList.remove("visibility-hidden");

      if (typeof window.formatMoney === "function") {
        if (salePrice) salePrice.innerHTML = window.formatMoney(price, moneyFormat);
        const compareValue = onSale ? compareAtPrice : price;
        compareAtPrices.forEach((node) => {
          node.innerHTML = window.formatMoney(compareValue, moneyFormat);
        });
      }
    }

    updateSoldOutState(card, variant) {
      const badge = card.querySelector(".m-product-tag--soldout");
      if (badge) badge.style.display = variant.available ? "none" : "flex";
    }

    async syncCard(card, optionValue, requestToken) {
      const handle = this.getCardHandle(card);
      const productData = await this.getProductData(handle);
      if (requestToken !== this.syncRequestToken) return false;

      const variant = this.findVariant(productData, optionValue);
      if (!variant) {
        card.dataset.relatedSyncMatched = "false";
        return false;
      }

      this.updateFallbackVariantState(card, variant);
      this.updateCardOptionState(card, productData, variant);
      this.updateCardImages(card, productData, variant);
      this.updateCardPrice(card, variant);
      this.updateSoldOutState(card, variant);
      card.dataset.relatedSyncMatched = "true";
      return true;
    }

    async syncCards(optionValue, requestToken) {
      const cards = Array.from(this.root.querySelectorAll(".m-product-card"));
      if (!cards.length) return;

      this.ensureCardOrder(cards);
      const matches = await Promise.all(
        cards.map((card) => this.syncCard(card, optionValue, requestToken))
      );
      if (requestToken !== this.syncRequestToken) return;

      this.sortCards(cards, matches);
      this.lastAppliedOptionValue = String(optionValue || "").trim();
      this.root.dataset.pairsSyncedValue = this.lastAppliedOptionValue;
    }
  }

  function initialize(root) {
    if (!root || root.dataset.pairsSyncController !== "v2") return;
    const controller = new PairsWellWithSyncController(root);
    controller.init();
  }

  function initializeAll(scope = document) {
    scope
      .querySelectorAll('[data-pairs-sync-controller="v2"]')
      .forEach((root) => initialize(root));
  }

  initializeAll();
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
